/*
https://www.algolia.com/doc/guides/building-search-ui/installation/js/
HTML based off of:
https://www.algolia.com/doc/api-reference/widgets/search-box/js/?client=With+a+package+manager#html-output
https://www.algolia.com/doc/api-reference/widgets/hits/js/#html-output
*/

import { stardust } from '@eidellev/adonis-stardust/client';
import instantsearch, { SearchClient } from 'instantsearch.js';
import { history } from 'instantsearch.js/es/lib/routers';
import {
  searchBox,
  sortBy,
  hits,
  pagination,
  configure,
  // dynamicWidgets,
  panel,
  refinementList,
  // rangeSlider,
  rangeInput,
  toggleRefinement,
  hierarchicalMenu,
  stats,
  clearRefinements,
} from 'instantsearch.js/es/widgets';
import aa from '../algolia-insights';
// import departmentHierarchalMenu from '../custom-widgets/departments-hierarchal-menu';
// import departmentMenu from '../custom-widgets/departments-menu';
import clearFilters from '../custom-widgets/clear-filters';
import liveRangeSlider from '../custom-widgets/live-range-slider';
// import heroTitle from '../custom-widgets/hero-title';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock-upgrade';
import {
  formatPrice, formatPriceNumber, generateTransformedImageUrl, PLACEHOLDER_IMAGE_URL,
} from '../../../../../app/Libraries/Utils';
import { getIndexName, buildSortByOptions } from '../../../../../app/Libraries/Algolia/AlgoliaUtils';
import {
  resolveHitPricing, visiblePromoLabels, MAPP_LABEL_COPY,
} from '../../../../../app/Libraries/MappCompliance';
import type { MappViewer } from '../../../../../app/Libraries/MappCompliance';
import {
  SPECIAL_OFFERS_WHITELIST,
  PAGE_SIZE,
  FILTERS_INITIAL_LIMIT,
  FILTERS_MAX_LIMIT,
} from '../../../../../app/Libraries/Algolia/constants';
import {
  getPageButtons,
  throttle,
  // generateArchivePageTitle,
} from '../../utils';
import { getWidgetForFilter } from './filters';
import { enableQidLinks } from '../qid-link';

/**
 * Initialize instantsearch.
 * Can accept a search client, but defaults to the one defined in this file.
 * @param searchClient Algolia search client
 */
export default function(searchClient: SearchClient) {
  enableQidLinks();

  // Login state can't change without a full page load, so resolve the viewer once. Both
  // attributes are written by layouts/main.edge from the middleware-shared `mappViewer`:
  // no data-cid → guest; data-cid + data-mapp-verified="false" → unverified customer.
  const viewer: MappViewer = {
    isGuest: !document.body.dataset.cid,
    isMappVerified: document.body.dataset.mappVerified === 'true',
  };
  const isGuest = viewer.isGuest;

  /**
   * Which attribute the promo filter facets on.
   *
   * Guests facet on `guestFilteredName`, which the index nulls out for `hidden` promos — so those
   * promos drop out of the sidebar AND out of the facet counts. Unverified customers facet on
   * `unverifiedFilteredName`, nulled for promos on MAPP-sensitive brands. Verified customers facet
   * on `name` and see everything.
   *
   * It has to be a separate ATTRIBUTE rather than filtering the rendered list with
   * `transformItems`: to filter client-side we'd have to tell the browser which promos are hidden,
   * which is exactly what `hidden` exists to prevent.
   *
   * Used in three places that must agree — the two routing mappers and the widget itself.
   */
  const PROMO_FACET = viewer.isGuest
    ? 'promotions.qualifier.guestFilteredName'
    : viewer.isMappVerified
      ? 'promotions.qualifier.name'
      : 'promotions.qualifier.unverifiedFilteredName';

  // This will come from the departments endpoint on page load...
  const additionalFilters = window.additionalFilters ?? [];
  // Take the department hierarchy and turn it into a series of filters, one for each lvl.
  const departmentHierarchy = window.departmentHierarchy ?? [];
  const departmentFilters = departmentHierarchy.map((_, index) => {
    const path = departmentHierarchy.slice(0, index + 1).map((d) => d.name).join(' > ');
    return `productGroup.menu.lvl${index}:"${path}"`;
  });

  // Load initial department hierarchalMenu state from page URL
  const initialDepartmentSelection = departmentHierarchy.length
    ? departmentHierarchy.map((d) => d.name).join(' > ')
    : undefined;
  const initialDepartmentSelectionArray = initialDepartmentSelection
    ? [initialDepartmentSelection]
    : undefined;

  // console.debug('additionalFilters', additionalFilters);
  const sidebarWidgets: any = additionalFilters.map((filter) => getWidgetForFilter(filter)());

  const search = instantsearch({
    indexName: getIndexName('products'),
    searchClient,
    // https://www.algolia.com/doc/libraries/search-insights
    insights: {
      insightsClient: aa,
    },
    // https://www.algolia.com/doc/guides/building-search-ui/going-further/routing-urls/js/
    // https://www.algolia.com/doc/api-reference/widgets/ui-state/js/
    routing: {
      router: history({
        cleanUrlOnDispose: true,
        // windowTitle(routeState) {
        //   const offersFilters = routeState.offers ? routeState.offers as string[] : [] as string[];
        //   const query = routeState.q as string;
        //   return generateArchivePageTitle({ offers: offersFilters, query, seoSuffix: true });
        // },
      }),
      stateMapping: {
        // @ts-ignore Idk what it's complaining about; this is straight from the docs
        stateToRoute(uiState) {
          // TODO: does this matter if it's a replica?
          const indexUiState = uiState[getIndexName('products')];
          // console.debug('indexUiState', indexUiState);

          // This is the format used below in routeToState
          const params = {
            q: indexUiState.query,
            page: indexUiState.page,

            // General filters
            brand: indexUiState.refinementList?.['brand.name'],
            price: indexUiState.range?.['price.base'],
            inStock: indexUiState.toggle?.['stock.inStock'],
            offers: indexUiState.refinementList?.['featured.description'],
            promotions: indexUiState.refinementList?.[PROMO_FACET],
            // NOTE: This looks like it's just storing level 0,
            // but that's how the Algolia hierarchical menu widget works
            department: indexUiState.hierarchicalMenu?.['productGroup.menu.lvl0'],

            // TODO: Add more filters as they come.
            // Probably come up with a generic function where I can specify nice query params
          };

          // Add any additional department-specific filters to the url params
          additionalFilters.forEach((filter: any) => {
            params[filter.slug] = indexUiState[filter.widgetType]?.[`specifications.${filter.slug}`];
          });

          return params;
        },
        // @ts-ignore Idk what it's complaining about; this is straight from the docs
        routeToState(routeState) {
          // TODO: does this matter if it's a replica?
          const indexName = getIndexName('products');

          const state = {
            query: routeState.q,
            page: routeState.page,

            // General filters
            refinementList: {
              'brand.name': routeState.brand,
              'featured.description': routeState.offers,
              // The URL param stays `?promotions=<name>` for everyone — only the attribute
              // differs. Both carry the same strings for non-hidden promos, so a shared filter
              // URL works across audiences. A link filtered on a HIDDEN promo gives a guest zero
              // results, which is the correct outcome for a promo they must not see.
              [PROMO_FACET]: routeState.promotions,
            },
            range: {
              // The paths here are [widget].[exact algolia attribute]
              'price.base': routeState.price,
            },
            toggle: {
              'stock.inStock': routeState.inStock,
            },
            hierarchicalMenu: {
              // NOTE: This looks like it's just storing level 0,
              // but that's how the Algolia hierarchical menu widget works
              'productGroup.menu.lvl0': routeState.department ?? initialDepartmentSelectionArray,
            },
            // menu: {},

            // TODO: Add more filters as they come.
            // Probably come up with a generic function where I can specify nice query params
          };

          // Add any additional department-specific filters to the state
          additionalFilters.forEach((filter: any) => {
            if (!state[filter.widgetType]) {
              state[filter.widgetType] = {};
            }
            state[filter.widgetType][`specifications.${filter.slug}`] = routeState[filter.slug];
          });

          return {
            [indexName]: state,
          };
        },
      },
    },
    // Some params to remove dev tools warnings
    future: {
      preserveSharedStateOnUnmount: true,
    },
  });

  function openFilters() {
    const filtersSidebar = document.querySelector<HTMLElement>('.js-filters');
    if (!filtersSidebar) {
      return;
    }

    // Disable scrolling everywhere except filters
    const targetElement = filtersSidebar.querySelector<HTMLElement>('.archive-filters__inner');
    if (targetElement) {
      disableBodyScroll(targetElement);
    }

    filtersSidebar.classList.add('open');
    filtersSidebar.setAttribute('aria-hidden', 'false');

    const pageBtns = getPageButtons();
    pageBtns.forEach((btn) => {
      btn.setAttribute('tabindex', '-1');
    });

    // Set tabindex of filter clickable elements to 0
    const filterLinks = filtersSidebar.querySelectorAll<HTMLElement>(
      'select, input, textarea, button, a, iframe'
    );
    filterLinks.forEach((link) => {
      link.setAttribute('tabindex', '0');
    });
    if (filterLinks.length > 0) {
      filterLinks[0].focus();
    }
  }

  function closeFilters(origFocus: HTMLElement | null) {
    const filtersSidebar = document.querySelector<HTMLElement>('.js-filters');
    if (!filtersSidebar) {
      return;
    }

    clearAllBodyScrollLocks();

    filtersSidebar.classList.remove('open');
    filtersSidebar.setAttribute('aria-hidden', 'false');

    const pageBtns = getPageButtons();
    pageBtns.forEach((btn) => {
      btn.setAttribute('tabindex', '0');
    });

    // Set tabindex of filter clickable elements to -1
    const filterLinks = filtersSidebar.querySelectorAll<HTMLElement>(
      'select, input, textarea, button, a, iframe'
    );
    filterLinks.forEach((link) => {
      link.setAttribute('tabindex', '-1');
    });
    if (filterLinks.length > 0) {
      filterLinks[0].focus();
    }

    // Move focus back to original focus before modal opened
    if (origFocus) {
      origFocus.focus();
    }
  }

  /**
   * Run the open/close filter panel animation, and toggle interaction
   */
  function toggleFilterPanel(panelEl: HTMLElement, open: boolean) {
    panelEl.setAttribute('aria-hidden', open ? 'false' : 'true');

    // set tabindex of filter clickable elements
    const panelBody = panelEl.querySelector('.ais-Panel-body') as HTMLElement;
    const filterLinks = panelBody.querySelectorAll<HTMLElement>(
      'select, input, textarea, button, a, iframe'
    );
    filterLinks?.forEach((link) => {
      link.setAttribute('tabindex', open ? '0' : '-1');
    });
  }

  // console.debug('departmentFilters', departmentFilters);

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  // https://www.algolia.com/doc/guides/building-search-ui/widgets/showcase/js/
  const widgetsToAdd: any = [
    configure({
      hitsPerPage: PAGE_SIZE,
      // Combine filters we want to apply but not put in the URL
      filters: (() => {
        const parts: string[] = [];
        // Add department filter
        if (departmentFilters.length > 0) {
          // We only really need the deepest lvl because it includes all the others
          parts.push(departmentFilters[departmentFilters.length - 1]);
        }
        // Add hidden deal filter (if present)
        const offerLabel = window.offer?.label;
        if (offerLabel) {
          parts.push(`featured.description:"${offerLabel}"`);
        }
        // Add brand filter (if present)
        const brandSlug = window.brand?.slug;
        if (brandSlug) {
          parts.push(`brand.slug:"${brandSlug}"`);
        }
        // Add series filter (if present)
        const seriesSlug = window.series?.slug;
        if (seriesSlug) {
          parts.push(`series.slug:"${seriesSlug}"`);
        }
        // Add promo filter (if present). The 'qualifier' view (the standard /promos/:slug archive)
        // filters to products that qualify the customer for the promo. The 'rewards' view
        // (/promos/:slug/qualifying-products) filters to products eligible as rewards.
        const promoSlug = window.promo?.slug;
        if (promoSlug) {
          const promoFacet = window.promo?.view === 'rewards' ? 'reward' : 'qualifier';
          parts.push(`promotions.${promoFacet}.slug:"${promoSlug}"`);
        }
        // Add product list filter (if present) — pre-filters to only products in this list
        const productListSlug = window.productList?.slug;
        if (productListSlug) {
          parts.push(`productLists.slug:"${productListSlug}"`);
        }
        // Add restricted-products filter (if present)
        if (window.restricted) {
          parts.push('restricted:true');
        }
        console.debug('=== algolia filters:', parts);
        return parts.length
          ? parts.join(' AND ')
          : undefined;
      })(),
    }),
    // This is hidden in the UI, but we need it for the search functionality and routing
    searchBox({
      container: '#hidden-search-box',
    }),
    // https://www.algolia.com/doc/api-reference/widgets/current-refinements/js/
    // heroTitle({
    //   // @ts-ignore-next-line (apparently the types haven't been added/updated for custom connectors yet?)
    //   container: document.querySelector('#archive-title'),
    // }),
    // https://www.algolia.com/doc/api-reference/widgets/sort-by/js/
    sortBy({
      container: '#sort-by',
      items: buildSortByOptions(),
    }),
    // https://www.algolia.com/doc/api-reference/widgets/hits/js/
    hits({
      container: '#hits',
      /**
       * Defensive: stardust throws synchronously when slug is empty, which
       * breaks the entire hits render. Drop slug-less records (indexer bug)
       */
      transformItems(items) {
        return items.filter((item) => {
          if (!item.slug) {
            console.warn('[archive] dropping hit with missing slug', { objectID: item.objectID, name: item.name });
            return false;
          }
          return true;
        });
      },
      templates: {
        item(hit, { html, components }) {
          // Clean URL in the href; the Algolia queryID rides along in data-qid
          // and is appended at click time (qid-link.ts) so crawlers don't
          // index ?qid= duplicates of every product page.
          const productUrl = stardust.route('shop.product', { slug: hit.slug });
          const qid = hit.__queryID ?? '';

          const stockSection = hit.stock?.inStock
            ? html`
            <p class="product-card__stock">
              In stock${hit.storeLocation?.aisle !== null ? `: Aisle ${hit.storeLocation.aisle}` : ''}
            </p>`
            : hit.stock?.canBeOverSold
              ? html`
              <p class="product-card__stock product-card__stock--backordered">
                0 in stock
              </p>`
              : html`
              <p class="product-card__stock product-card__stock--invalid">
                Out of stock
              </p>`;

          /**
           * These prices will be the exact ones we need for either:
           * - A user, if logged in (we're fetching and injecting the data)
           * - A guest, if not logged in (Algolia will have the base price)
           */
          // The index exposes the product's promo aggregate on `hit.promotions`; the shared
          // MappCompliance module (same implementation the server uses) turns it into a treatment.
          // Products with no `promotions` object resolve to fully visible.
          // Price hiding is guest-only; badge suppression is per viewer.
          const mapp = resolveHitPricing(hit.promotions, isGuest);
          const pricingHidden = mapp.hidePricing;
          // Badges are filtered PER PROMO — a cart-only promo drops its own badge and leaves
          // an unrelated visible promo's alone. See visiblePromoLabels().
          const promoLabels = visiblePromoLabels(hit.promotions, viewer);
          // console.debug('hit.promotions', hit.promotions);

          const price = hit.price?.discount ?? hit.price?.base ?? 0;
          // Default compareAt is 0, which will be skipped as falsey
          const compareAt = hit.price?.discount ? hit.price.base : 0;

          const [priceDollars, priceCents] = formatPriceNumber(price).split('.');

          const priceSection = pricingHidden
            ? html`
              <div class="price price--hidden">
                ${MAPP_LABEL_COPY[mapp.label]
    ? html`<span class="price-sticker">${MAPP_LABEL_COPY[mapp.label]}</span>`
    : ''}
              </div>`
            : html`
            <div class="price ${compareAt ? 'price--sale' : ''}">
              ${compareAt ? html`<s>${formatPrice(compareAt, 'usd', true)}</s>` : ''}
              <div class="price__current">
                <sup class="price__dollar-sign">$</sup>${priceDollars}<sup class="price__cents">.${priceCents}</sup>
                <span class="price__text">each</span>
              </div>
            </div>`;

          const seriesSection = hit.series
            ? html`<span class="product-card__series">${hit.series.name}</span>`
            : '';

          // TODO: Is it possible to keep these in sync with the edge component somehow?
          const featuredBanner = hit.featured ? html`<span class="product-card__banner" style="color: ${hit.featured.foregroundColour}; background-color: ${hit.featured.backgroundColour};">${hit.featured.name}</span>` : '';
          const featuredBannerListView = hit.featured ? html`<span class="product-card__badge product-card__badge--featured" style="color: ${hit.featured.foregroundColour}; background-color: ${hit.featured.backgroundColour};">${hit.featured.name}</span>` : '';
          // We're just rendering the first one because it will be pre-sorted by priority —
          // the first SURVIVING one, since suppressed promos are already filtered out above.
          // NOTE: Algolia hits have promotions modeled with parallel arrays under qualifier/reward
          // nesting: { qualifier: { label: string[]; slug: string[]; name: string[] }, reward: { slug: string[] } }
          // so that we can use it for faceting. The API data is still promotions: Promotion[].
          const promoBadges = promoLabels[0]
            ? html`<span class="product-card__badge product-card__badge--promo">${promoLabels[0]}</span>`
            : '';

          // License-restricted products (e.g. pesticides) can only be purchased by a
          // logged-in customer with a valid pesticide license (body data attribute).
          // The card's marker is Millbank's `featured` tag; only the add to cart is gated here.
          const restrictedBlocked = !!hit.restricted
            && document.body.dataset.hasPesticideLicense !== 'true';

          const imageUrlToUse = hit.imageUrl || PLACEHOLDER_IMAGE_URL;
          const imageSection = html`
            <picture class="product-card__image">
              <source
                data-srcset="
                  ${generateTransformedImageUrl(imageUrlToUse, { format: 'webp', fit: 'scale-down', width: 300 })} 1x,
                  ${generateTransformedImageUrl(imageUrlToUse, { format: 'webp', fit: 'scale-down', width: 600 })} 2x
                "
                sizes="300px"
                type="image/webp"
              />
              <source
                data-srcset="
                  ${generateTransformedImageUrl(imageUrlToUse, { format: 'jpeg', fit: 'scale-down', width: 300 })} 1x,
                  ${generateTransformedImageUrl(imageUrlToUse, { format: 'jpeg', fit: 'scale-down', width: 600 })} 2x
                "
                sizes="300px"
                type="image/jpeg"
              />
              <img
                data-src="${generateTransformedImageUrl(imageUrlToUse, { format: 'jpeg', fit: 'scale-down', width: 300 })}"
                data-srcset="
                  ${generateTransformedImageUrl(imageUrlToUse, { format: 'jpeg', fit: 'scale-down', width: 300 })} 1x,
                  ${generateTransformedImageUrl(imageUrlToUse, { format: 'jpeg', fit: 'scale-down', width: 600 })} 2x
                "
                sizes="300px"
                alt="${hit.imageUrl ? '' : 'No product image available'}"
                class="lazyload"
              />
            </picture>
          `;

          return html`
          <div class="product-card ${(!hit.stock?.inStock && !hit.stock?.canBeOverSold) ? 'product-card--oos' : ''}">
            <a href="${productUrl}" data-qid="${qid}" class="product-card__image-wrapper">
              ${featuredBanner}
              ${imageSection}
            </a>

            <form method="POST" action="/api/cart"
              onSubmit="${
                (e) => {
                  e.preventDefault(); // Prevent the default form submission from navigating away
                  window.submitAddToCartForm(e.target);
                }}"
            >
              <input type="hidden" name="id" value="${hit.objectID}" />
              <input type="hidden" name="_csrf" value="${csrfToken}" />

              <div class="product-card__details">
                ${ hit.brand.name === 'Generic' ? '' :
                  html`
                  <span class="product-card__brand">
                    ${hit.brand.name}${seriesSection}
                  </span>`
                }
                <h3 class="product-card__title">
                  <a href="${productUrl}" data-qid="${qid}" class="product-card__link">
                    ${components.Highlight({ attribute: 'name', hit })}
                  </a>
                </h3>
                <div class="product-card__meta-wrapper">
                  <div class="product-card__meta">
                    <span class="product-card__meta-item">SKU #${hit.sku}</span>
                    ${/* Aleks said we could leave this quantity out if we don't have the data for it
                      <span class="product-card__meta-item">Format 45 PC</span>
                    */ ''}
                  </div>
                  ${featuredBannerListView}
                  ${promoBadges}
                </div>
                <div class="product-card__price-stock">
                  ${priceSection}
                  ${stockSection}
                </div>
                <div class="product-card__btns">
                  <a href="${productUrl}" data-qid="${qid}" class="product-card__btn btn btn--secondary">
                    <span>View</span><span>Product</span>
                  </a>
                  <button class="btn-atc" type="submit" disabled=${(!hit.stock?.inStock && !hit.stock?.canBeOverSold) || restrictedBlocked}>
                    <span class="sr-only">Add to Cart</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="27.166" height="24.73" viewBox="0 0 27.166 24.73" class="icon btn-atc__icon"><path d="M19.93 22.547H8.819a2.938 2.938 0 0 1-2.834 2.182 2.937 2.937 0 0 1-2.932-2.937 2.936 2.936 0 0 1 2.867-2.931l.715-2.372-2.621-13.7L0 1.406l.488-1.41 4.836 1.649.572 3.009 21.27 2.36-2.9 10.261h-16.3l-.584 1.938a2.949 2.949 0 0 1 1.436 1.826h11.111a2.937 2.937 0 0 1 2.834-2.182 2.936 2.936 0 0 1 2.932 2.932 2.937 2.937 0 0 1-2.932 2.937 2.938 2.938 0 0 1-2.833-2.179Zm1.4-.755a1.431 1.431 0 0 0 1.432 1.433 1.431 1.431 0 0 0 1.432-1.433 1.435 1.435 0 0 0-1.432-1.432 1.435 1.435 0 0 0-1.429 1.432Zm-16.779 0a1.431 1.431 0 0 0 1.432 1.433 1.433 1.433 0 0 0 1.434-1.433 1.436 1.436 0 0 0-1.434-1.432 1.435 1.435 0 0 0-1.427 1.432Zm3.475-6.013h15.1l2.109-7.468L6.19 6.196Z" /></svg>
                  </button>
                </div>
              </div>
            </form>
          </div>`;
        },
        empty(results, { html }) {
          return html`
          <p class="hits__empty-heading">
            Sorry, we found no results${results.query && html` for <q>${results.query}</q>`}
          </p>
          <span>Try the following:</span>
          <ul>
            <li>Check spelling</li>
            <li>Try different keywords</li>
            <li>Try more general keywords</li>
            <li>Use fewer filters</li>
          </ul>`;
        },
      },
    }),
    // In-Stock widget
    panel({ templates: { header(_options, { html }) {
      return html`In Stock`;
    } }, collapsed: () => false })
    (toggleRefinement)({ container: '#in-stock-widget', attribute: 'stock.inStock', on: true, templates: {
      labelText({ onFacetValue }, { html }) {
        return html`<span>In stock (${onFacetValue.count?.toLocaleString()})</span>`;
      },
    } }),
    // This originally had the brand and in-stock widgets here, but it was causing a grid flickering issue
    // because the dynamicWidgets makes a second Algolia API call
    // // https://www.algolia.com/doc/api-reference/widgets/dynamic-facets/js/?client=default#examples
    // // Basically this will pull in and display whatever you have set for faceting in the algolia index settings
    // // So you need to set those up and the facet display order, or they won't show up
    // dynamicWidgets({
    //   container: '#dynamic-widgets',
    //   widgets: [],
    // }),
    // Price slider
    // panel({ templates: { header: 'Price' }, collapsed: () => false })
    liveRangeSlider({
      container: '#price-slider',
      inputsContainer: '#price-input',
      attribute: 'price.base',
      pips: false,
      tooltips: false,
      // {
      //   format(rawValue: number) {
      //     return formatPrice(rawValue, 'usd', false);
      //   },
      // },
    }),
    // Price input
    rangeInput({ container: '#price-input', attribute: 'price.base', templates: {
      submitText(_, { html }) {
        return html`<span>Apply</span>`;
      },
    } }),
    // https://www.algolia.com/doc/api-reference/widgets/clear-refinements/js/
    clearFilters({
      // @ts-ignore: For some reason it doesn't like the container param
      container: document.getElementById('clear-filters-desktop'),
    }),
    clearFilters({
      // @ts-ignore: For some reason it doesn't like the container param
      container: document.getElementById('clear-filters-mobile'),
    }),
    // https://www.algolia.com/doc/api-reference/widgets/pagination/js/
    pagination({
      container: '#pagination',
      templates: {
        first: () => 'First',
        last: () => 'Last',
        previous() {
          return '<svg xmlns="http://www.w3.org/2000/svg" width="8.41" height="14.608" viewBox="0 0 8.41 14.608"><path d="M6.961 14.608 0 7.328 6.959-.001l1.45 1.378-5.647 5.946 5.645 5.9Z" fill="currentColor"/></svg>';
        },
        next() {
          return '<svg xmlns="http://www.w3.org/2000/svg" width="8.41" height="14.608" viewBox="0 0 8.41 14.608"><path d="M1.448 14.608.002 13.226l5.645-5.9L0 1.377 1.45-.001l6.96 7.329Z" fill="currentColor"/></svg>';
        },
      },
      padding: 2,
      scrollTo: '.archive-wrapper',
    }),
  ];

  // Add the departments hierarchal menu if the container exists on the page
  const departmentWidget = document.getElementById('departments-widget');
  if (departmentWidget) {
    widgetsToAdd.push(
      panel({ templates: { header(_options, { html }) {
        return html`Department`;
      } }, collapsed: () => false })
      (hierarchicalMenu)({
        container: '#departments-widget',
        attributes: [
          'productGroup.menu.lvl0',
          'productGroup.menu.lvl1',
          'productGroup.menu.lvl2',
        ],
        // The Millbank team wants it to show all the departments
        limit: 100,
        // Only refinementList supports searchable param
        // searchable: true,
      }),
    );
  }

  // Add Brand widget if the container exists on the page
  const brandWidget = document.getElementById('brand-widget');
  if (brandWidget) {
    widgetsToAdd.push(
      panel({ templates: { header(_options, { html }) {
        return html`Brand`;
      } }, collapsed: () => false })
      (refinementList)({
        container: '#brand-widget',
        attribute: 'brand.name',
        searchable: true,
        limit: FILTERS_INITIAL_LIMIT,
        showMore: true,
        showMoreLimit: FILTERS_MAX_LIMIT,
      }),
    );
  }

  // Add Promotions widget if the container exists on the page
  // The container is omitted from the template on promo archive pages, where results are already pre-filtered
  const promotionsWidget = document.getElementById('promotions-widget');
  if (promotionsWidget) {
    widgetsToAdd.push(
      panel({ templates: { header(_options, { html }) {
        return html`Promotions`;
      } }, collapsed: () => false })
      (refinementList)({
        container: '#promotions-widget',
        attribute: PROMO_FACET,
        searchable: true,
        limit: FILTERS_INITIAL_LIMIT,
        showMore: true,
        showMoreLimit: FILTERS_MAX_LIMIT,
      }),
    );
  }

  // Add Lists widget if the container exists on the page
  // The container is omitted from the template on product list archive pages, where results are already pre-filtered
  const listsWidget = document.getElementById('lists-widget');
  if (listsWidget) {
    widgetsToAdd.push(
      panel({ templates: { header(_options, { html }) {
        return html`Lists`;
      } }, collapsed: () => false })
      (refinementList)({
        container: '#lists-widget',
        attribute: 'productLists.name',
        searchable: true,
        limit: FILTERS_INITIAL_LIMIT,
        showMore: true,
        showMoreLimit: FILTERS_MAX_LIMIT,
      }),
    );
  }

  // Add Special Offers widget if the container exists on the page
  const offersWidget = document.getElementById('offers-widget');
  if (offersWidget) {
    widgetsToAdd.push(
      panel({ templates: { header(_options, { html }) {
        return html`Special Offers`;
      } }, collapsed: () => false })
      (refinementList)({
        container: '#offers-widget',
        attribute: 'featured.description',
        searchable: false,
        limit: FILTERS_INITIAL_LIMIT,
        showMore: true,
        showMoreLimit: FILTERS_MAX_LIMIT,
        transformItems(items) {
          return items.filter((item) => SPECIAL_OFFERS_WHITELIST.includes(item.label));
        },
      }),
    );
  }

  const queryStatsContainer = document.getElementById('query-stats');
  if (queryStatsContainer) {
    widgetsToAdd.push(
      stats({
        container: '#query-stats',
        templates: {
          text(data, { html }) {
            if (!data.query) {
              return '';
            }
            let text = data.nbHits === 1
              ? 'result for'
              : 'results for';
            return html`
            <p class="archive__query-text">
              <strong>${data.nbHits}</strong> ${text} “${data.query}”
            </p>`;
          },
        },
      }),
      clearRefinements({
        container: '#clear-query',
        includedAttributes: ['query'],
        templates: {
          resetLabel(_, { html }) {
            // TODO: The icon is @!component('svg/icons-ui', {type: 'close'}), but how can I inject that here?
            return html`
            <span class="search-clear-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="21.507" height="21.507" viewBox="0 0 21.507 21.507" class="icon"><path d="m10.753 12.518-8.984 8.984-1.768-1.768 8.982-8.986L.001 1.764 1.769-.004l8.984 8.982 8.98-8.982 1.773 1.768-8.984 8.984 8.984 8.986-1.773 1.768Z"/></svg>
            </span>
            Clear search`;
          },
        },
        cssClasses: {
          button: 'archive__query-clear',
        },
      }),
    );
  }

  // Add the main UI widgets and default dynamicWidgets
  search.addWidgets(widgetsToAdd);

  // Add any custom sidebar widgets for this page
  search.addWidgets(sidebarWidgets);

  const updateFilterScrollCue = (panelBody: HTMLElement) => {
    const hasOverflow = panelBody.scrollHeight - panelBody.clientHeight > 1;
    const isScrollEnd = panelBody.scrollTop + panelBody.clientHeight >= panelBody.scrollHeight - 2;

    panelBody.classList.toggle('has-scroll-cue', hasOverflow);
    panelBody.classList.toggle('is-scroll-end', !hasOverflow || isScrollEnd);
  };

  const syncFilterScrollCues = () => {
    const panelBodies = document.querySelectorAll<HTMLElement>('.ais-Panel-body');
    panelBodies.forEach((panelBody) => {
      if (!panelBody.dataset.scrollCueBound) {
        panelBody.addEventListener('scroll', () => {
          updateFilterScrollCue(panelBody);
        }, { passive: true });
        panelBody.dataset.scrollCueBound = 'true';
      }

      updateFilterScrollCue(panelBody);
    });
  };

  let shouldRecalculateOpenPanelHeights = false;

  const recalculateOpenPanelHeights = () => {
    const openPanelBodies = document.querySelectorAll<HTMLElement>(
      '.ais-Panel:not(.ais-Panel--collapsed) .ais-Panel-body'
    );
    console.debug('Recalculating open panel heights for panels:', openPanelBodies);

    openPanelBodies.forEach((panelBody) => {
      // Only apply to facet list panels that can become scrollable.
      const hasFacetList = !!panelBody.querySelector('.ais-RefinementList, .ais-HierarchicalMenu');
      if (!hasFacetList) {
        return;
      }

      const previousMaxHeight = panelBody.style.maxHeight;
      panelBody.style.maxHeight = '';

      // Wait one frame so layout settles after InstantSearch updates.
      requestAnimationFrame(() => {
        const recalculatedHeight = panelBody.clientHeight;
        if (recalculatedHeight > 0) {
          const recalculatedHeightPx = `${recalculatedHeight}px`;
          panelBody.style.maxHeight = recalculatedHeightPx;
          panelBody.dataset.openMaxHeight = recalculatedHeightPx;
          return;
        }

        panelBody.style.maxHeight = previousMaxHeight;
      });
    });
  };

  search.on('render', () => {
    syncFilterScrollCues();

    if (shouldRecalculateOpenPanelHeights) {
      shouldRecalculateOpenPanelHeights = false;
      setTimeout(() => {
        recalculateOpenPanelHeights();
      }, 500);
    }
  });

  search.start();

  const lockPanelBodyOpenHeight = (panelBody: HTMLElement, force = false) => {
    const renderedHeight = panelBody.clientHeight;
    if (renderedHeight <= 0) {
      return false;
    }

    const heightPx = `${renderedHeight}px`;
    if (force || !panelBody.dataset.openMaxHeight) {
      panelBody.style.maxHeight = heightPx;
      panelBody.dataset.openMaxHeight = heightPx;
    }

    return true;
  };

  const initializePanelBodyOpenHeight = (panelBody: HTMLElement) => {
    let attempts = 0;
    const maxAttempts = 20;

    const tryLock = () => {
      attempts += 1;

      if (lockPanelBodyOpenHeight(panelBody)) {
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(tryLock, 100);
        return;
      }

      // Last resort for edge cases where layout hasn't resolved in time.
      const fallbackHeightPx = `${panelBody.scrollHeight}px`;
      panelBody.style.maxHeight = fallbackHeightPx;
      panelBody.dataset.openMaxHeight = fallbackHeightPx;
    };

    tryLock();
  };

  let origFocus: HTMLElement | null = null;

  // Filters sidebar
  const filtersBtn = document.querySelector('.js-open-filter') as HTMLButtonElement;
  const filterCloseBtns = document.querySelectorAll('.js-close-filters');

  // Hook up price panel to the standard "ais-Panel--collapsed" class toggle etc.
  // This makes it so that it uses the standard collapse functionality as the other panels
  const customPanels = document.querySelectorAll<HTMLElement>('.filters-accordion.ais-Panel');
  customPanels.forEach((panel) => {
    const panelBody = panel.querySelector<HTMLElement>('.ais-Panel-body');
    if (!panelBody) {
      return;
    }

    // Set initial visibility
    panelBody.classList.add('ais-Panel--collapsible');
    panelBody.setAttribute('aria-hidden', 'false');

    const panelButton = panel.querySelector<HTMLButtonElement>('.ais-Panel-collapseButton');
    if (!panelButton) {
      return;
    }

    panelButton.setAttribute('aria-expanded', 'true');

    // Mark so the Algolia setTimeout handler skips this panel and avoids a duplicate listener
    panel.dataset.customPanel = 'true';

    panelButton.addEventListener('click', (e) => {
      e.preventDefault();
      panel.classList.toggle('ais-Panel--collapsed');
      const isNowCollapsed = panel.classList.contains('ais-Panel--collapsed');

      if (isNowCollapsed) {
        // Save current open height before collapsing so re-open can restore it
        panelBody.dataset.openMaxHeight = panelBody.style.maxHeight || `${panelBody.scrollHeight}px`;
        panelBody.style.maxHeight = '0';
      } else {
        // Always use live scrollHeight so late-rendered slider content is never clipped
        panelBody.style.maxHeight = `${panelBody.scrollHeight}px`;
      }

      panelBody.setAttribute('aria-hidden', String(isNowCollapsed));
      panelButton.setAttribute('aria-expanded', String(!isNowCollapsed));

      setTimeout(() => syncFilterScrollCues(), 350);
    });
  });

  filtersBtn.addEventListener('click', (e) => {
    // filtersSidebar.classList.toggle('filters-sidebar--open');
    e.preventDefault();
    origFocus = e.target as HTMLElement;

    openFilters();
  });

  filterCloseBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      closeFilters(origFocus);
    });
  });

  // Scroll to top of filters when click reset button
  const resetFiltersBtn = document.getElementById('clear-filters-mobile');
  resetFiltersBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    const filtersSidebarInner = document.querySelector('.archive-filters__inner') as HTMLElement;
    filtersSidebarInner.scrollTo(0, 0);
  });

  // TODO: These widgets don't exist on page load, so I'm just adding a wait for now...
  // Ideally we'd do something like adding an onclick handler via the algolia template,
  // but doesn't look like we can without using a custom panel widget? Which is more time than we have right now.
  setTimeout(() => {
    // Initialise open height for custom panels (e.g. price) now that Algolia content has rendered.
    // Their click handlers use live scrollHeight so they don't need the Algolia forEach below.
    customPanels.forEach((panel) => {
      const panelBody = panel.querySelector<HTMLElement>('.ais-Panel-body');
      if (panelBody) {
        initializePanelBodyOpenHeight(panelBody);
      }
    });

    const filterPanelButtons = document.querySelectorAll('.ais-Panel-collapseButton');
    filterPanelButtons.forEach((button) => {
      const panel = button.closest('.ais-Panel') as HTMLElement;
      // Skip custom panels — they have their own click handler with max-height management
      if (panel.dataset.customPanel) {
        return;
      }

      const panelBody = panel.querySelector('.ais-Panel-body') as HTMLElement;

      // Set initial max-height for any panels that are expanded by default.
      // Use rendered height so a quick "Show more" can't lock the panel at full scrollHeight.
      initializePanelBodyOpenHeight(panelBody);

      button.addEventListener('click', (e) => {
        e.preventDefault();
        const showPanel = !panel.classList.contains('ais-Panel--collapsed');

        if (showPanel) {
          const savedOpenHeight = panelBody.dataset.openMaxHeight;
          const fallbackHeight = `${panelBody.scrollHeight}px`;
          const fallbackHeightNum = panelBody.scrollHeight;
          const savedHeightNum = savedOpenHeight ? parseFloat(savedOpenHeight) : 0;

          // Prevent re-open from growing to full facet height after "Show more".
          // If content shrank since last open, clamp to the current content height.
          const reopenHeight = savedOpenHeight && savedHeightNum > 0
            ? `${Math.min(savedHeightNum, fallbackHeightNum)}px`
            : fallbackHeight;
          panelBody.style.maxHeight = reopenHeight;
        } else {
          const currentOpenHeight = panelBody.style.maxHeight;
          if (currentOpenHeight && currentOpenHeight !== '0px') {
            panelBody.dataset.openMaxHeight = currentOpenHeight;
          }
          panelBody.style.maxHeight = '0';
        }

        toggleFilterPanel(panel, showPanel);

        setTimeout(() => {
          syncFilterScrollCues();
        }, 350);
      });
    });

    // Make the price input fields apply automatically
    const priceDiv = document.getElementById('price-input');
    if (priceDiv) {
      const inputs = priceDiv.querySelectorAll<HTMLInputElement>('input.ais-RangeInput-input');
      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          const applyButton = priceDiv.querySelector<HTMLButtonElement>('button.ais-RangeInput-submit');
          if (applyButton) {
            applyButton.click();
          }
        });
      });
    }
  }, 1000);

  const filtersContainer = document.querySelector<HTMLElement>('.js-filters');
  filtersContainer?.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const hierarchyLink = event.target.closest('.ais-HierarchicalMenu-link');
    if (hierarchyLink) {
      shouldRecalculateOpenPanelHeights = true;
    }

    const showMoreButton = event.target.closest('.ais-RefinementList-showMore');
    if (!showMoreButton) {
      return;
    }

    const panelBody = showMoreButton.closest('.ais-Panel-body') as HTMLElement | null;
    if (panelBody && !panelBody.dataset.openMaxHeight) {
      lockPanelBodyOpenHeight(panelBody, true);
    }

    requestAnimationFrame(() => {
      syncFilterScrollCues();
    });
  }, true);

  // @md in css
  const breakpoint = 992;
  // Close filters sidebar when not on mobile
  window.addEventListener('resize', throttle(() => {
    if (window.matchMedia(`(min-width: ${breakpoint}px)`).matches) {
      closeFilters(null);

      const filters = document.querySelector('.js-filters');

      // set tabindex of filter clickable elements to 0
      const filterLinks = filters?.querySelectorAll(
        'select, input, textarea, button, a, iframe'
      ) as NodeListOf<HTMLElement>;
      filterLinks?.forEach((link) => {
        link.setAttribute('tabindex', '0');
      });
      if (filterLinks) {
        filterLinks[0].focus();
      }
    }

    syncFilterScrollCues();

    // console.debug('state', search.getUiState());
    // console.debug('backendState', algoliaUiStateToBackendParams(search.getUiState()));
  }, 100));
}
